import type { ReactNode } from 'react';
import { Broadcast } from './Broadcast';
import { Header } from '../Header';
import { Footer } from '../Footer';

type MainProps = {
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
    podlets?: Record<string, any> | undefined;
    broadcastUrl?: string | undefined;
    className?: string | undefined;
    customContainer?: boolean;
    children: ReactNode;
};

export const Main = ({ podlets, broadcastUrl, className, customContainer = false, children }: MainProps) => {
    return (
        <WrapPodlets podlets={podlets}>
            <main className={`${customContainer ? '' : 'page-container '}${className ?? ''}`}>
                {broadcastUrl && <Broadcast url={broadcastUrl} />}
                {children}
            </main>
        </WrapPodlets>
    );
};

export const MainStatic = ({ className, children }: Omit<MainProps, 'podlets' | 'broadcastUrl'>) => (
    <>
        <Header />
        <main data-testid="Main-test-id" className={className ?? 'page-container'}>
            {children}
        </main>
        <Footer />
    </>
);

const WrapPodlets = ({ podlets, children }) => {
    return podlets ? (
        <>
            {podlets.header ? <div suppressHydrationWarning={true} {...podlets.header.props} /> : <Header />}
            <div suppressHydrationWarning={true} {...podlets.advertising?.props} />
            {children}
            {podlets.footer ? <div suppressHydrationWarning={true} {...podlets.footer.props} /> : <Footer />}
            {podlets.js?.map((script) => (
                <script {...script.props} key={script.key} crossOrigin="" defer />
            ))}
        </>
    ) : (
        children
    );
};

// Two 'page-container' areas with different background colors, common for admin pages. Fabric only.
export const MainSplit = ({ top, podlets, broadcastUrl, className, children }: Omit<MainProps, 'troika'> & { top: ReactNode }) => {
    const renderMain = () => (
        <>
            <section className={`page-container ${className ?? ''}`}>
                {broadcastUrl && <Broadcast url={broadcastUrl} />}
                {top}
            </section>
            <main className="s-bg-hover border-t s-border">
                <div className={`page-container s-bg-hover ${className ?? ''}`}>{children}</div>
            </main>
        </>
    );

    return podlets ? (
        <>
            {podlets.header ? <div {...podlets.header.props} /> : <Header />}
            <div {...podlets.advertising?.props} />
            {renderMain()}
            {podlets.footer ? <div {...podlets.footer.props} /> : <Footer />}
            {podlets.js?.map((script) => (
                <script {...script.props} key={script.key} crossOrigin="" defer />
            ))}
        </>
    ) : (
        renderMain()
    );
};
